import React from 'react';

/**
 * Eight candidate directions for the Watchtower mark, for review.
 * All are 24x24, drawn in currentColor, and weighted to hold at 16px.
 */

export interface MarkVariant {
  id: string;
  name: string;
  note: string;
  Mark: (props: {
    className?: string;
  }) => JSX.Element;
}
const svg = (children: React.ReactNode, className: string) => <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" focusable="false" data-id="element-198" data-mp="2y71cra613ikw">
    {children}
  </svg>;

/* A — the current mark: solid platform, tapered body, lookout window. */
function BeaconTower({
  className = ''
}: {
  className?: string;
}) {
  return svg(<>
      <circle cx="12" cy="3.1" r="1.35" fill="currentColor" data-id="element-199" data-mp="3c3etgh2qnfyb" />
      <path d="M7.5 5.5a6.3 6.3 0 0 1 9 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.55" data-id="element-200" data-mp="3h39maq62qfm0" />
      <path d="M4.4 8.1h15.2l-2.1 3H6.5z" fill="currentColor" data-id="element-201" data-mp="1mqa3ypab77b1" />
      <path fillRule="evenodd" clipRule="evenodd" d="M7.9 12.4h8.2l1.3 8.1H6.6zm3.1 2.3h2v3.3h-2z" fill="currentColor" data-id="element-202" data-mp="1msnv4n1vbzdb" />
      <rect x="5.2" y="21.1" width="13.6" height="1.7" rx="0.6" fill="currentColor" data-id="element-203" data-mp="39ezz1tnnmg9q" />
    </>, className);
}

/* B — lighthouse: the beam is the idea, thrown wide to both sides. */
function Lighthouse({
  className = ''
}: {
  className?: string;
}) {
  return svg(<>
      <path d="M9.4 5.2 1.8 2.6v5.2zM14.6 5.2l7.6-2.6v5.2z" fill="currentColor" opacity="0.4" data-id="element-204" data-mp="zg9p5q3thzy" />
      <path d="M8.7 3.2h6.6v4.2H8.7z" fill="currentColor" data-id="element-205" data-mp="3n7fvgbm1vg1o" />
      <path d="M6.6 8.6h10.8l-1.4 2.4H8z" fill="currentColor" data-id="element-206" data-mp="3n5v04jrwvt2r" />
      <path fillRule="evenodd" clipRule="evenodd" d="M8.6 12.2h6.8l1.5 8.6H7.1zm2.4 2.4h2v3.2h-2z" fill="currentColor" data-id="element-207" data-mp="3j6dvfskb6zyn" />
      <rect x="5" y="21.3" width="14" height="1.6" rx="0.6" fill="currentColor" data-id="element-208" data-mp="2bq6dxumhey30" />
    </>, className);
}

/* C — the tower reduced to a monogram W by its own legs. */
function MonogramW({
  className = ''
}: {
  className?: string;
}) {
  return svg(<>
      <circle cx="12" cy="3.4" r="1.5" fill="currentColor" data-id="element-209" data-mp="1z9owwuqgedqy" />
      <path d="M4.6 7.4h14.8l-2 3H6.6z" fill="currentColor" data-id="element-210" data-mp="1z95bh41lh786" />
      <path d="m5.4 12.6 2.6 8 4-6.2 4 6.2 2.6-8" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" data-id="element-211" data-mp="2488gwplhlfgo" />
    </>, className);
}

/* D — signal only: the tower is implied by the gap between the arcs. */
function SignalStack({
  className = ''
}: {
  className?: string;
}) {
  return svg(<>
      <path d="M6.8 4.6a9.4 9.4 0 0 1 10.4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity="0.4" data-id="element-212" data-mp="1lia0nm7jcg1y" />
      <path d="M8.4 8.1a6.3 6.3 0 0 1 7.2 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity="0.7" data-id="element-213" data-mp="17jamj8hnfjk1" />
      <circle cx="12" cy="12" r="2.2" fill="currentColor" data-id="element-214" data-mp="swtq66tpwxbn" />
      <path d="m9.6 15.4-1.5 6.2M14.4 15.4l1.5 6.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" data-id="element-215" data-mp="nwpb8l8gkanj" />
      <path d="M9.1 19h5.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" opacity="0.5" data-id="element-216" data-mp="11y1pms7z047b" />
    </>, className);
}

/* E — tower enclosed in a shield: the security read, stated plainly. */
function ShieldTower({
  className = ''
}: {
  className?: string;
}) {
  return svg(<>
      <path d="M12 1.8 3.4 4.6v6.6c0 5 3.6 9.1 8.6 11 5-1.9 8.6-6 8.6-11V4.6z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" data-id="element-217" data-mp="1kmyiqsrmsqny" />
      <circle cx="12" cy="6.6" r="1.15" fill="currentColor" data-id="element-218" data-mp="1gnhaxsoqfhfm" />
      <path d="M7.7 9.4h8.6l-1.3 2.1H9z" fill="currentColor" data-id="element-219" data-mp="1ulxjzqa2hk0x" />
      <path fillRule="evenodd" clipRule="evenodd" d="M9.5 12.6h5l0.9 4.9H8.6zm1.7 1.6h1.6v2.1h-1.6z" fill="currentColor" data-id="element-220" data-mp="1pmb7jj22c7t6" />
    </>, className);
}

/* F — outline throughout: lighter on the page, more architectural. */
function LineTower({
  className = ''
}: {
  className?: string;
}) {
  return svg(<>
      <circle cx="12" cy="3" r="1.4" fill="currentColor" data-id="element-221" data-mp="2ih0w855u4exn" />
      <path d="M8.2 5.6a5.6 5.6 0 0 1 7.6 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" data-id="element-222" data-mp="2ng3pkscgcwps" />
      <path d="M4.6 8.4h14.8l-2.2 3H6.8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" data-id="element-223" data-mp="1qro2d2wtezfu" />
      <path d="M7.8 12.8h8.4l1.2 8H6.6z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" data-id="element-224" data-mp="1zoycum1t6kjp" />
      <path d="M10.6 15.4h2.8v3.4h-2.8z" stroke="currentColor" strokeWidth="1.4" opacity="0.6" data-id="element-225" data-mp="24mzvj5vbohun" />
      <path d="M4.9 22h14.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" data-id="element-226" data-mp="3g3q5n02v9ttj" />
    </>, className);
}

/* G — the tower stands on a curved horizon: watching the whole estate. */
function HorizonTower({
  className = ''
}: {
  className?: string;
}) {
  return svg(<>
      <circle cx="12" cy="2.9" r="1.3" fill="currentColor" data-id="element-227" data-mp="uq1ijk81v0aw" />
      <path d="M8.4 5.3a5.4 5.4 0 0 1 7.2 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.55" data-id="element-228" data-mp="ps9ooknuh442" />
      <path d="M6.4 7.7h11.2l-1.7 2.7H8.1z" fill="currentColor" data-id="element-229" data-mp="loboe6ms9n9f" />
      <path fillRule="evenodd" clipRule="evenodd" d="M8.9 11.6h6.2l1 6.6H7.9zm2.3 1.9h1.6v2.7h-1.6z" fill="currentColor" data-id="element-230" data-mp="gqaae047se4t" />
      <path d="M2.4 19.4a13.5 13.5 0 0 0 19.2 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" data-id="element-231" data-mp="6tdv9bjt8tra" />
      <path d="M5.6 21.4v1.4M18.4 21.4v1.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" data-id="element-232" data-mp="265q3k61qtp92" />
    </>, className);
}

/* H — the lookout window is a keyhole: the SSL read, built in. */
function KeyholeTower({
  className = ''
}: {
  className?: string;
}) {
  return svg(<>
      <circle cx="12" cy="3.1" r="1.3" fill="currentColor" data-id="element-233" data-mp="3lph70fz8mzqu" />
      <path d="M8.6 5.5a5.3 5.3 0 0 1 6.8 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.55" data-id="element-234" data-mp="3ln4j7m6stv5v" />
      <path d="M5 8h14l-2 2.9H7z" fill="currentColor" data-id="element-235" data-mp="i9scu7shxd7x" />
      <path fillRule="evenodd" clipRule="evenodd" d="M7.6 12.1h8.8l1.2 8.6H6.4zm4.4 2.3a1.55 1.55 0 0 0-.85 2.85l-.55 2.15h2.8l-.55-2.15A1.55 1.55 0 0 0 12 14.4z" fill="currentColor" data-id="element-236" data-mp="n7k5j40yt3d6" />
      <rect x="5.2" y="21.2" width="13.6" height="1.6" rx="0.6" fill="currentColor" data-id="element-237" data-mp="d8miy6n3njwt" />
    </>, className);
}
export const markVariants: MarkVariant[] = [{
  id: 'A',
  name: 'Beacon Tower',
  note: 'Current. Solid, symmetrical, reads as a tower immediately.',
  Mark: BeaconTower
}, {
  id: 'B',
  name: 'Lighthouse',
  note: 'The beam does the talking. Warmest and most narrative of the set.',
  Mark: Lighthouse
}, {
  id: 'C',
  name: 'Monogram W',
  note: 'The legs form a W. Doubles as a letterform, strongest as a favicon.',
  Mark: MonogramW
}, {
  id: 'D',
  name: 'Signal Stack',
  note: 'Tower implied, transmission stated. Most abstract and most technical.',
  Mark: SignalStack
}, {
  id: 'E',
  name: 'Shield Tower',
  note: 'Security read stated outright. Safest, also the most conventional.',
  Mark: ShieldTower
}, {
  id: 'F',
  name: 'Line Tower',
  note: 'Outline throughout. Lighter on the page, architectural, quieter.',
  Mark: LineTower
}, {
  id: 'G',
  name: 'Horizon Tower',
  note: 'Stands on a curved horizon — watching an estate, not one site.',
  Mark: HorizonTower
}, {
  id: 'H',
  name: 'Keyhole Tower',
  note: 'Lookout window becomes a keyhole. Ties the tower to the SSL job.',
  Mark: KeyholeTower
}];